import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { describePeriod, formatInt, formatMoneyCompact, isNum } from '../format';
import { pagePath } from '../siteConfig';
import useSeoDocumentTitle from '../useSeoDocumentTitle';
import GapChip from '../components/GapChip';
import { SeoFooter } from '../components/Panels';
import { FreshnessLine, SegmentedControl } from '../components/Primitives';

const SORTS = [
  { value: 'name', label: 'A–Z' },
  { value: 'above', label: 'Most above guide' },
  { value: 'below', label: 'Closest to guide' },
  { value: 'price', label: 'Highest price' },
];

const compare = {
  name: (a, b) => a.name.localeCompare(b.name),
  above: (a, b) => (b.guide_gap_pct ?? -1e9) - (a.guide_gap_pct ?? -1e9),
  below: (a, b) => Math.abs(a.guide_gap_pct ?? 1e9) - Math.abs(b.guide_gap_pct ?? 1e9),
  price: (a, b) => (b.median_sale_price ?? -1) - (a.median_sale_price ?? -1),
};

/** Normalise for search: case, accents and punctuation don't matter. */
const norm = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export default function SuburbIndexPage({ payload }) {
  useSeoDocumentTitle('suburb-index', payload);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('name');

  const all = useMemo(() => payload.lgas.flatMap((g) => g.suburbs), [payload.lgas]);
  const measured = all.filter((s) => s.indexable).length;

  const groups = useMemo(() => {
    const q = norm(query);
    return payload.lgas
      .map((g) => ({
        ...g,
        suburbs: g.suburbs
          .filter((s) => !q || norm(s.name).includes(q) || (s.postcode || '').startsWith(q))
          .sort(compare[sort]),
      }))
      .filter((g) => g.suburbs.length);
  }, [payload.lgas, query, sort]);

  const shown = groups.reduce((sum, g) => sum + g.suburbs.length, 0);

  return (
    <main className="re-seo re-seo--index" id="main">
      <div className="re-seo__inner">
        <header className="re-masthead">
          <p className="re-kicker">Sydney suburbs</p>
          <h1>Where homes sell above their price guide</h1>
          <p className="re-lede">
            {formatInt(all.length)} suburbs, {formatInt(measured)} with enough sales to measure. Each figure is the median
            gap between the sale price and the advertised guide over {describePeriod(payload.data_period)}.
          </p>
          <FreshnessLine period={payload.data_period} />
        </header>

        <div className="re-index-controls">
          <label className="re-search">
            <span className="re-sr-only">Find a suburb</span>
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <circle cx="8.5" cy="8.5" r="5.75" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <path d="M13 13 L17.5 17.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a suburb or postcode"
              autoComplete="off"
            />
          </label>
          <SegmentedControl label="Sort suburbs" options={SORTS} value={sort} onChange={setSort} />
        </div>

        <p className="re-muted re-index-count" role="status">
          {query ? `${formatInt(shown)} match${shown === 1 ? '' : 'es'}` : ' '}
        </p>

        <div className="re-index">
          {groups.map((g) => (
            <section key={g.slug} className="re-index__group" aria-labelledby={`lga-${g.slug}`}>
              <h2 id={`lga-${g.slug}`}>
                {g.name} <span className="re-muted">{formatInt(g.suburbs.length)}</span>
              </h2>
              <ul>
                {g.suburbs.map((s) => (
                  <li key={s.slug} className={s.indexable ? '' : 'is-limited'}>
                    <Link to={pagePath('suburb', s.slug)}>
                      <span className="re-index__name">
                        {s.name}
                        {s.postcode ? <span className="re-index__postcode">{s.postcode}</span> : null}
                      </span>
                      <span className="re-index__gap">
                        {s.indexable ? <GapChip value={s.guide_gap_pct} /> : <span className="re-muted">Limited data</span>}
                      </span>
                      <span className="re-index__price">
                        {isNum(s.median_sale_price) ? formatMoneyCompact(s.median_sale_price) : ''}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {!groups.length ? <p className="re-empty">No suburbs match &ldquo;{query}&rdquo;.</p> : null}
        </div>

        <SeoFooter dataVersion={payload.data_version} />
      </div>
    </main>
  );
}
